import React, { useState, useEffect } from 'react';
import {
    Drawer,
    IconButton,
    Typography,
    Box,
    TextField,
    Button,
    CircularProgress,
    Divider,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem
} from '@mui/material';
import { Close, AccountTree, Add, Delete, Edit } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';

const RelationshipDrawer = ({ open, onClose, headerId, headerName }) => {
    const [relationships, setRelationships] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentRelationship, setCurrentRelationship] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [relationshipToDelete, setRelationshipToDelete] = useState(null);

    // Metadata for dropdowns
    const [allTables, setAllTables] = useState([]);
    const [fkColumns, setFkColumns] = useState([]);
    const [pkColumns, setPkColumns] = useState([]);

    const [formData, setFormData] = useState({
        fkTableName: '',
        fkColumnName: '',
        pkTableName: '',
        pkColumnName: '',
        relationshipType: 'MANY_TO_ONE',
        confidenceLevel: 'CONFIRMED',
        description: ''
    });

    const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/global-link-relationships`;

    useEffect(() => {
        if (open && headerId) {
            fetchRelationships();
            fetchTableMetadata();
        }
    }, [open, headerId]);

    // Fetch FK columns when FK table changes
    useEffect(() => {
        if (formData.fkTableName) {
            fetchColumnsForTable(formData.fkTableName, 'fk');
        } else {
            setFkColumns([]);
        }
    }, [formData.fkTableName]);

    // Fetch PK columns when PK table changes
    useEffect(() => {
        if (formData.pkTableName) {
            fetchColumnsForTable(formData.pkTableName, 'pk');
        } else {
            setPkColumns([]);
        }
    }, [formData.pkTableName]);

    const fetchTableMetadata = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/metadata/tables`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const tables = await response.json();
                setAllTables(tables);
            }
        } catch (error) {
            console.error('Error fetching table metadata:', error);
        }
    };

    const fetchColumnsForTable = async (tableName, type) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/metadata/tables/${tableName}/columns`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.ok) {
                const columns = await response.json();
                if (type === 'fk') {
                    setFkColumns(columns);
                } else {
                    setPkColumns(columns);
                }
            }
        } catch (error) {
            console.error(`Error fetching columns for ${tableName}:`, error);
        }
    };

    const fetchRelationships = async () => {
        if (!headerId) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/headers/${headerId}/relationships`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setRelationships(data);
        } catch (error) {
            console.error('Error fetching relationships:', error);
            toast.error(`Failed to load relationships: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleAddRelationship = async () => {
        if (!formData.fkTableName.trim() || !formData.fkColumnName.trim() ||
            !formData.pkTableName.trim() || !formData.pkColumnName.trim()) {
            toast.warning('All table and column fields are required');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/relationships`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...formData,
                    linkHeaderId: headerId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setRelationships([...relationships, data]);
            resetForm();
            setDialogOpen(false);
            toast.success('Relationship added successfully');
        } catch (error) {
            console.error('Error adding relationship:', error);
            toast.error(`Failed to add relationship: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEditRelationship = (relationship) => {
        setCurrentRelationship(relationship);
        setFormData({
            fkTableName: relationship.fkTableName,
            fkColumnName: relationship.fkColumnName,
            pkTableName: relationship.pkTableName,
            pkColumnName: relationship.pkColumnName,
            relationshipType: relationship.relationshipType,
            confidenceLevel: relationship.confidenceLevel,
            description: relationship.description || ''
        });
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleUpdateRelationship = async () => {
        if (!formData.fkTableName.trim() || !formData.fkColumnName.trim() ||
            !formData.pkTableName.trim() || !formData.pkColumnName.trim()) {
            toast.warning('All table and column fields are required');
            return;
        }

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/relationships/${currentRelationship.relationshipId}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const updatedRelationship = await response.json();
            setRelationships(relationships.map(rel =>
                rel.relationshipId === currentRelationship.relationshipId ? updatedRelationship : rel
            ));

            resetForm();
            setEditMode(false);
            setCurrentRelationship(null);
            setDialogOpen(false);
            toast.success('Relationship updated successfully');
        } catch (error) {
            console.error('Error updating relationship:', error);
            toast.error(`Failed to update relationship: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRelationship = (relationship) => {
        setRelationshipToDelete(relationship);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!relationshipToDelete) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/relationships/${relationshipToDelete.relationshipId}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            setRelationships(relationships.filter(rel => rel.relationshipId !== relationshipToDelete.relationshipId));
            toast.success('Relationship deleted successfully');

            if (currentRelationship && currentRelationship.relationshipId === relationshipToDelete.relationshipId) {
                resetForm();
                setEditMode(false);
                setCurrentRelationship(null);
                setDialogOpen(false);
            }
        } catch (error) {
            console.error('Error deleting relationship:', error);
            toast.error(`Failed to delete relationship: ${error.message}`);
        } finally {
            setLoading(false);
            setDeleteDialogOpen(false);
            setRelationshipToDelete(null);
        }
    };

    const resetForm = () => {
        setFormData({
            fkTableName: '',
            fkColumnName: '',
            pkTableName: '',
            pkColumnName: '',
            relationshipType: 'MANY_TO_ONE',
            confidenceLevel: 'CONFIRMED',
            description: ''
        });
    };

    const handleCancel = () => {
        resetForm();
        setEditMode(false);
        setCurrentRelationship(null);
        setDialogOpen(false);
    };

    const handleOpenDialog = () => {
        resetForm();
        setEditMode(false);
        setCurrentRelationship(null);
        setDialogOpen(true);
    };

    return (
        <>
            <Drawer
                anchor="right"
                open={open}
                onClose={onClose}
                sx={{ '& .MuiDrawer-paper': { width: 850 } }}
            >
                <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box display="flex" alignItems="center">
                            <AccountTree sx={{ mr: 1 }} />
                            <Typography variant="h6">Relationships</Typography>
                        </Box>
                        <IconButton onClick={onClose}>
                            <Close />
                        </IconButton>
                    </Box>

                    <Typography variant="subtitle1" gutterBottom>
                        Link: {headerName || 'Unknown'}
                    </Typography>

                    <Divider sx={{ my: 2 }} />

                    {/* Styled Container for Grid - mimicking DynamicContainer */}
                    <Paper
                        sx={{
                            flex: 1,
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        }}
                    >
                        {/* Header Bar */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            backgroundColor: 'transparent',
                        }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Relationship List
                            </Typography>
                            <IconButton
                                color="primary"
                                onClick={handleOpenDialog}
                                size="small"
                            >
                                <Add />
                            </IconButton>
                        </Box>

                        {/* Grid Content */}
                        <Box sx={{ flex: 1, overflow: 'hidden', px: 2, pb: 2 }}>
                            {loading && !relationships.length ? (
                                <Box display="flex" justifyContent="center" my={4}>
                                    <CircularProgress />
                                </Box>
                            ) : relationships.length === 0 ? (
                                <Box sx={{ p: 2, textAlign: 'center' }}>
                                    <Typography color="textSecondary">
                                        No relationships found for this link
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ height: '100%', width: '100%' }}>
                                    <DataGrid
                                        rows={relationships.map(rel => ({
                                            ...rel,
                                            id: rel.relationshipId
                                        }))}
                                        columns={[
                                            {
                                                field: 'foreignKey',
                                                headerName: 'Foreign Key',
                                                width: 240,
                                                renderCell: (params) => `${params.row.fkTableName}.${params.row.fkColumnName}`
                                            },
                                            {
                                                field: 'primaryKey',
                                                headerName: 'Primary Key',
                                                width: 240,
                                                renderCell: (params) => `${params.row.pkTableName}.${params.row.pkColumnName}`
                                            },
                                            { field: 'relationshipType', headerName: 'Type', width: 130 },
                                            { field: 'confidenceLevel', headerName: 'Confidence', width: 120 },
                                            {
                                                field: 'actions',
                                                headerName: 'Actions',
                                                width: 100,
                                                sortable: false,
                                                filterable: false,
                                                renderCell: (params) => (
                                                    <Box>
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleEditRelationship(params.row)}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeleteRelationship(params.row)}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )
                                            }
                                        ]}
                                        pageSize={10}
                                        rowsPerPageOptions={[10, 25, 50]}
                                        disableSelectionOnClick
                                        density="compact"
                                        sx={{
                                            border: 'none',
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: '#F8F8F8',
                                                color: '#6B0F2A',
                                                fontWeight: 600,
                                                fontSize: '0.875rem',
                                            },
                                            '& .MuiDataGrid-cell': {
                                                fontSize: '0.8125rem',
                                            },
                                            '& .MuiDataGrid-row:hover': {
                                                backgroundColor: 'rgba(139, 159, 232, 0.08)',
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Paper>
                </Box>
            </Drawer>

            <Dialog open={dialogOpen} onClose={handleCancel} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editMode ? 'Edit Relationship' : 'Add Relationship'}
                    <IconButton
                        aria-label="close"
                        onClick={handleCancel}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Box>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            label="Foreign Key Table"
                            value={formData.fkTableName}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    fkTableName: e.target.value,
                                    fkColumnName: '' // Reset column when table changes
                                });
                            }}
                            disabled={loading}
                            margin="normal"
                            required
                            helperText="Select the table containing the foreign key"
                        >
                            {allTables.map((table) => (
                                <MenuItem key={table} value={table}>
                                    {table}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            label="Foreign Key Column"
                            value={formData.fkColumnName}
                            onChange={(e) => setFormData({ ...formData, fkColumnName: e.target.value })}
                            disabled={loading || !formData.fkTableName}
                            margin="normal"
                            required
                            helperText={!formData.fkTableName ? "Select FK table first" : "Select the foreign key column"}
                        >
                            {fkColumns.map((column) => (
                                <MenuItem key={column} value={column}>
                                    {column}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            label="Primary Key Table"
                            value={formData.pkTableName}
                            onChange={(e) => {
                                setFormData({
                                    ...formData,
                                    pkTableName: e.target.value,
                                    pkColumnName: '' // Reset column when table changes
                                });
                            }}
                            disabled={loading}
                            margin="normal"
                            required
                            helperText="Select the table containing the primary key (referenced table)"
                        >
                            {allTables
                                .filter(table => table !== formData.fkTableName) // Exclude FK table
                                .map((table) => (
                                    <MenuItem key={table} value={table}>
                                        {table}
                                    </MenuItem>
                                ))}
                        </TextField>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            label="Primary Key Column"
                            value={formData.pkColumnName}
                            onChange={(e) => setFormData({ ...formData, pkColumnName: e.target.value })}
                            disabled={loading || !formData.pkTableName}
                            margin="normal"
                            required
                            helperText={!formData.pkTableName ? "Select PK table first" : "Select the primary key column"}
                        >
                            {pkColumns.map((column) => (
                                <MenuItem key={column} value={column}>
                                    {column}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            label="Relationship Type"
                            value={formData.relationshipType}
                            onChange={(e) => setFormData({ ...formData, relationshipType: e.target.value })}
                            disabled={loading}
                            margin="normal"
                        >
                            <MenuItem value="ONE_TO_ONE">One to One</MenuItem>
                            <MenuItem value="ONE_TO_MANY">One to Many</MenuItem>
                            <MenuItem value="MANY_TO_ONE">Many to One</MenuItem>
                            <MenuItem value="MANY_TO_MANY">Many to Many</MenuItem>
                        </TextField>
                        <TextField
                            fullWidth
                            size="small"
                            select
                            label="Confidence Level"
                            value={formData.confidenceLevel}
                            onChange={(e) => setFormData({ ...formData, confidenceLevel: e.target.value })}
                            disabled={loading}
                            margin="normal"
                        >
                            <MenuItem value="CONFIRMED">Confirmed</MenuItem>
                            <MenuItem value="HIGH">High</MenuItem>
                            <MenuItem value="MEDIUM">Medium</MenuItem>
                            <MenuItem value="LOW">Low</MenuItem>
                        </TextField>
                        <TextField
                            fullWidth
                            size="small"
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Optional description"
                            disabled={loading}
                            margin="normal"
                            multiline
                            rows={2}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancel}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={editMode ? handleUpdateRelationship : handleAddRelationship}
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : editMode ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this relationship?
                    </Typography>
                    {relationshipToDelete && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {relationshipToDelete.fkTableName}.{relationshipToDelete.fkColumnName} → {relationshipToDelete.pkTableName}.{relationshipToDelete.pkColumnName}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RelationshipDrawer;
